import type { PointerEvent as ReactPointerEvent, RefObject } from "react";
import { toast } from "sonner";
import { flushSync } from "react-dom";
import type { SetState } from "../../vendor/src/features/remotion-editor/editor/context-provider";
import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import { getVisibleFrames } from "../../vendor/src/features/remotion-editor/editor/utils/get-visible-frames";
import { getCompositionDuration } from "../../vendor/src/features/remotion-editor/editor/utils/get-composition-duration";
import { timelineScrollContainerRef } from "../../vendor/src/features/remotion-editor/editor/utils/restore-scroll-after-zoom";
import {
  collectSnapPoints,
  findNearestSnapPoint,
} from "../../vendor/src/features/remotion-editor/editor/timeline/utils/snap-points";
import { generateRandomId } from "../../vendor/src/features/remotion-editor/editor/utils/generate-random-id";
import { getOffsetOfTrack } from "../../vendor/src/features/remotion-editor/editor/utils/position-utils";
import { resizeTimelineSelection } from "./timeline-resize";
import { itemOwner } from "./group-split";
const sessions = new WeakSet<object>();

/** Window-level pointer session survives leaving a handle, handles cancel, and commits one undo step. */
export function startPointerTrim({
  event,
  stateRef,
  setState,
  itemId,
  side,
  timelineWidth,
  height,
  onEnd,
}: {
  event: ReactPointerEvent<HTMLDivElement>;
  stateRef: RefObject<EditorState>;
  setState: SetState;
  itemId: string;
  side: "left" | "right";
  timelineWidth: number;
  height: number;
  onEnd: () => void;
}): (() => void) | null {
  const base = stateRef.current,
    item = base.undoableState.items[itemId],
    owner = itemOwner(base, itemId);
  if (
    sessions.has(stateRef) ||
    !item ||
    !owner ||
    owner.track.locked ||
    timelineWidth <= 0
  ) {
    onEnd();
    return null;
  }
  sessions.add(stateRef);
  const token = "trim-" + generateRandomId(16),
    pointerId = event.pointerId,
    startX = event.clientX;
  const initialScroll = timelineScrollContainerRef.current?.scrollLeft ?? 0;
  const selected = base.selectedItems.includes(itemId)
    ? base.selectedItems
    : [itemId];
  const visible = getVisibleFrames({
    fps: base.undoableState.fps,
    totalDurationInFrames: getCompositionDuration(
      base.undoableState.items,
      base.undoableState.tracks,
    ),
  });
  const points = collectSnapPoints({
    tracks: base.undoableState.tracks,
    items: base.undoableState.items,
    excludeItemIds: selected,
  });
  const index = base.undoableState.tracks.findIndex(
    (track) => track.id === owner.track.id,
  );
  const trimming = {
    itemId,
    side,
    trackIndex: index,
    top: getOffsetOfTrack({
      trackIndex: index,
      tracks: base.undoableState.tracks,
      items: base.undoableState.items,
    }),
    height,
    minFrom: 0,
    maxDurationInFrames: null,
  };
  let ended = false,
    started = false,
    established = false,
    raf = 0,
    pending: PointerEvent | null = null,
    lastInput: EditorState | null = null,
    lastEdge: number | null = null;
  const owns = (current: EditorState) =>
    current === lastInput ||
    current.documentMutationOwner === token ||
    (!established && current.undoableState === base.undoableState);
  const cleanup = () => {
    sessions.delete(stateRef);
    cancelAnimationFrame(raf);
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    window.removeEventListener("pointercancel", cancel);
    window.removeEventListener("keydown", key, true);
    window.removeEventListener("blur", cancel);
  };
  const finish = (rollback: boolean, conflict = false, synchronous = false) => {
    if (ended) return;
    ended = true;
    cleanup();
    if (started) {
      const publish = () =>
        setState({
          mutationOwner: token,
          update: (current) => {
            const restore = rollback && owns(current);
            return {
              ...current,
              undoableState: restore
                ? base.undoableState
                : current.undoableState,
              selectedItems: restore
                ? base.selectedItems
                : current.selectedItems,
              activeSelectionSurface: restore
                ? base.activeSelectionSurface
                : current.activeSelectionSurface,
              itemsBeingTrimmed: current.itemsBeingTrimmed.filter(
                (trim) => trim.itemId !== itemId,
              ),
              activeSnapPoint: null,
              timelineTimeEditApplied: true,
            };
          },
          commitToUndoStack: !rollback || conflict,
        });
      // Pointer/key completion must commit the visible tree before follow-up UI reads.
      // Unmount cleanup deliberately avoids flushSync inside React's lifecycle.
      if (synchronous) flushSync(publish);
      else publish();
    }
    if (conflict)
      toast.info(
        "다른 편집이 적용되어 드래그를 멈췄습니다. 현재 상태를 확인한 뒤 다시 조정해 주세요.",
      );
    else if (
      !rollback &&
      !started &&
      lastEdge !== null &&
      lastEdge !==
        (side === "left" ? item.from : item.from + item.durationInFrames)
    )
      toast.info(
        item.type === "group"
          ? "그룹 안의 클립을 먼저 늘려 주세요."
          : "원본 구간 또는 선택한 클립의 최소 길이에 도달했습니다.",
      );
    onEnd();
  };
  const render = (pointer: PointerEvent) => {
    if (ended) return;
    const current = stateRef.current;
    if (!owns(current) || current.isLinkingEnabled !== base.isLinkingEnabled) {
      finish(true, true, true);
      return;
    }
    const scroll =
      (timelineScrollContainerRef.current?.scrollLeft ?? 0) - initialScroll;
    if (!started && Math.abs(pointer.clientX - startX + scroll) < 2) return;
    const delta = Math.round(
      ((pointer.clientX - startX + scroll) / timelineWidth) * visible,
    );
    const original =
      side === "left" ? item.from : item.from + item.durationInFrames;
    const snap =
      current.isSnappingEnabled && !pointer.altKey
        ? findNearestSnapPoint({
            targetFrame: original + delta,
            snapPoints: points,
            thresholdInFrames: (6 / timelineWidth) * visible,
          })
        : null;
    const edge = snap?.frame ?? original + delta;
    if (edge === lastEdge) return;
    lastEdge = edge;
    const planned = resizeTimelineSelection(
      base,
      selected,
      side,
      edge - original,
    );
    if (!started && planned === base) return;
    if (!started) {
      setState({ update: (state) => state, commitToUndoStack: true });
      started = true;
    }
    setState({
      mutationOwner: token,
      update: (state) => {
        if (!owns(state)) return state;
        lastInput = state;
        established = true;
        return {
          ...state,
          undoableState: planned.undoableState,
          selectedItems: planned.selectedItems,
          activeSelectionSurface: "timeline",
          itemsBeingTrimmed: [
            ...state.itemsBeingTrimmed.filter((trim) => trim.itemId !== itemId),
            trimming,
          ],
          activeSnapPoint: snap,
          timelineTimeEditApplied: true,
        };
      },
      commitToUndoStack: false,
    });
  };
  function tick() {
    if (ended) return;
    const container = timelineScrollContainerRef.current;
    if (!container?.isConnected) {
      finish(true);
      return;
    }
    if (pending) {
      if (started) {
        const rect = container.getBoundingClientRect();
        if (pending.clientX < rect.left + 12) container.scrollLeft -= 5;
        else if (pending.clientX > rect.right - 12) container.scrollLeft += 5;
      }
      render(pending);
    }
    if (!ended) raf = requestAnimationFrame(tick);
  }
  function move(pointer: PointerEvent) {
    if (pointer.pointerId !== pointerId) return;
    pending = pointer;
    if (!raf) raf = requestAnimationFrame(tick);
  }
  function up(pointer: PointerEvent) {
    if (pointer.pointerId !== pointerId) return;
    cancelAnimationFrame(raf);
    render(pointer);
    finish(false, false, true);
  }
  function cancel(event?: Event) {
    finish(true, false, !!event);
  }
  function key(e: KeyboardEvent) {
    if (
      e.code === "Escape" ||
      (e.code === "KeyZ" && (e.metaKey || e.ctrlKey))
    ) {
      e.preventDefault();
      e.stopPropagation();
      finish(true, false, true);
    }
  }
  event.preventDefault();
  event.stopPropagation();
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
  window.addEventListener("pointercancel", cancel);
  window.addEventListener("keydown", key, true);
  window.addEventListener("blur", cancel);
  return cancel;
}
